import { useEffect, useState } from 'react'

import { openFile } from '../lib/api'
import { errorText } from '../lib/offsite'
import { useSession } from '../lib/SessionContext'

/* ============================================================================
   กล่องข่าว/ประกาศ สำหรับหน้าแรก (Home.tsx) — ฝั่งพนักงาน
   API: GET /api/news/feed · backend/src/routes/adminNews.js → lib/news.feed()

   แต่ละหมวดเป็นกล่อง "พับเปิด-ปิดได้" เหมือนหน้าเดิม (HomeSite3 q-expansion-item)
   ใช้ <details> ของเบราว์เซอร์ตรง ๆ ไม่ต้องมี state/JS เอง
   หมวดที่ปักหมุด/ข่าวใหม่เปิดไว้ก่อน ที่เหลือพับ — จะได้ไม่ยาวจนเลื่อนหาเมนู

   ไม่มีลิงก์ภายนอกแล้ว — ทุกอย่างเป็นไฟล์ใน DB (/api/news/:id/file)
   รายการที่ยังไม่มีไฟล์ (รอ HR อัป) โชว์เป็นข้อความเฉย ๆ กดไม่ได้
   ============================================================================ */

type Item = {
  id: number
  title: string
  pinned: boolean
  hasFile: boolean
}
type Group = { category: string; categoryTh: string; items: Item[] }

/** กล่องหมวดหนึ่งกล่อง (พับได้) — ใช้ทั้งคอลัมน์ซ้ายและขวา */
function renderGroup(g: Group) {
  // หมวดที่มีของใหม่ติดป้ายไว้ แต่ทุกกล่องเริ่มแบบ "พับ" ให้ user กดเปิดเอง
  const hasNew = g.items.some((it) => it.pinned)
  return (
    <details key={g.category} className="news-group">
      <summary>
        {g.categoryTh}
        <span className="news-count">{g.items.length}</span>
        {hasNew && <span className="chip chip--warn">ใหม่</span>}
      </summary>
      <ul className="news-list">
        {g.items.map((it) => (
          <li key={it.id}>
            {it.pinned && <span className="chip chip--warn">ใหม่</span>}
            {it.hasFile ? (
              <button
                type="button"
                className="linklike"
                onClick={() => void openFile(`/api/news/${it.id}/file`).catch(() => {})}
              >
                {it.title}
              </button>
            ) : (
              // ยังไม่มีไฟล์ (ไฟล์เก่าตาย รอ HR อัปใหม่) — โชว์ชื่อเฉย ๆ
              <span className="news-nofile">{it.title}</span>
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}

export default function NewsFeed() {
  const { call } = useSession()
  const [groups, setGroups] = useState<Group[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    call<{ groups: Group[] }>('/api/news/feed')
      .then((d) => alive && setGroups(d.groups))
      .catch((err) => alive && setError(errorText(err)))
    return () => {
      alive = false
    }
  }, [call])

  // ยังโหลดอยู่ หรือไม่มีข่าวเลย → ไม่ต้องแสดงอะไร (หน้าแรกไม่ควรมีกล่องเปล่า)
  if (error) return <p className="error">{error}</p>
  if (!groups || groups.length === 0) return null

  // แบ่งซ้าย-ขวาเหมือนหน้าเดิม (HomeSite3 มี col-md-6 สองฝั่ง)
  // ครึ่งแรกของลำดับหมวด (news/ประกาศ/นโยบาย) ซ้าย · ที่เหลือ (กฎ/ฟอร์ม/คลังความรู้) ขวา
  // บนจอแคบ CSS ยุบเหลือคอลัมน์เดียวเอง
  const mid = Math.ceil(groups.length / 2)
  const left = groups.slice(0, mid)
  const right = groups.slice(mid)

  return (
    <section className="card news-feed">
      <h2>ข่าวสารและประกาศ</h2>
      <div className="news-cols">
        <div className="news-col">{left.map(renderGroup)}</div>
        <div className="news-col">{right.map(renderGroup)}</div>
      </div>
    </section>
  )
}
